import { Injectable } from '@angular/core';
import type Keycloak from 'keycloak-js';
import { ConfigurationService } from '../config/configuration.service';
import { LoggerService } from '../services/logger.service';
import { AuthRedirectReason } from './auth-redirect-reason';

export type AuthRedirectDebugDetails = Record<string, unknown>;

type RefreshOutcome = 'success' | 'error' | 'never_attempted';

interface AuthSessionTimeline {
    loginAt?: number;
    lastRefreshAttemptAt?: number;
    lastRefreshSuccessAt?: number;
    lastRefreshErrorAt?: number;
    lastTokenExpiredAt?: number;
    lastUserActivityAt?: number;
    refreshAttemptCount: number;
    lastRefreshOutcome: RefreshOutcome;
}

@Injectable({
    providedIn: 'root',
})
export class AuthRedirectDebugService {
    private enabled = false;
    private keycloakInstance?: Keycloak;
    private timeline: AuthSessionTimeline = this.createEmptyTimeline();

    constructor(
        private logger: LoggerService,
        private configurationService: ConfigurationService
    ) {}

    configure(enabled: boolean): void {
        this.enabled = enabled;
    }

    isEnabled(): boolean {
        return this.enabled;
    }

    setKeycloakInstance(instance: Keycloak | undefined): void {
        this.keycloakInstance = instance;
    }

    markLogin(): void {
        if (!this.enabled) {
            return;
        }

        this.timeline = this.createEmptyTimeline();
        this.timeline.loginAt = Date.now();
    }

    markRefreshAttempt(): void {
        if (!this.enabled) {
            return;
        }

        this.timeline.lastRefreshAttemptAt = Date.now();
        this.timeline.refreshAttemptCount += 1;
    }

    markRefreshSuccess(): void {
        if (!this.enabled) {
            return;
        }

        this.timeline.lastRefreshSuccessAt = Date.now();
        this.timeline.lastRefreshOutcome = 'success';
    }

    markRefreshError(): void {
        if (!this.enabled) {
            return;
        }

        this.timeline.lastRefreshErrorAt = Date.now();
        this.timeline.lastRefreshOutcome = 'error';
    }

    markTokenExpired(): void {
        if (!this.enabled) {
            return;
        }

        this.timeline.lastTokenExpiredAt = Date.now();
    }

    markUserActivity(): void {
        if (!this.enabled) {
            return;
        }

        this.timeline.lastUserActivityAt = Date.now();
    }

    log(reason: AuthRedirectReason, details: AuthRedirectDebugDetails = {}): void {
        if (!this.enabled) {
            return;
        }

        this.logger.debug('[AuthRedirect]', JSON.stringify({
            reason,
            timestamp: new Date().toISOString(),
            url: window.location.href,
            ...this.buildTimingSnapshot(),
            ...details,
        }));
    }

    private createEmptyTimeline(): AuthSessionTimeline {
        return {
            refreshAttemptCount: 0,
            lastRefreshOutcome: 'never_attempted',
        };
    }

    private buildTimingSnapshot(): AuthRedirectDebugDetails {
        const now = Date.now();
        const msSince = (at?: number): number | undefined =>
            at !== undefined ? now - at : undefined;

        let accessTokenExp: string | undefined;
        let accessTokenExpiresInSeconds: number | undefined;
        let refreshTokenPresent: boolean | undefined;

        const keycloak = this.keycloakInstance;
        if (keycloak?.tokenParsed?.exp) {
            accessTokenExp = new Date(keycloak.tokenParsed.exp * 1000).toISOString();
            accessTokenExpiresInSeconds =
                keycloak.tokenParsed.exp - Math.floor(now / 1000);
        }

        if (keycloak) {
            refreshTokenPresent = !!keycloak.refreshToken;
        }

        let configSnapshot: AuthRedirectDebugDetails = {};
        try {
            const config = this.configurationService.getConfig();
            configSnapshot = {
                sessionIdleSeconds: config.auth.sessionIdleSeconds,
                tokenMinValiditySeconds: config.auth.tokenMinValiditySeconds,
                tokenRefreshIntervalSeconds: config.auth.tokenRefreshIntervalSeconds,
                checkLoginIframe: config.auth.checkLoginIframe,
            };
        } catch {
            // Configuration not loaded yet.
        }

        return {
            sessionMsSinceLogin: msSince(this.timeline.loginAt),
            sessionMsSinceLastRefreshSuccess: msSince(this.timeline.lastRefreshSuccessAt),
            sessionMsSinceLastRefreshAttempt: msSince(this.timeline.lastRefreshAttemptAt),
            sessionMsSinceLastRefreshError: msSince(this.timeline.lastRefreshErrorAt),
            sessionMsSinceLastTokenExpired: msSince(this.timeline.lastTokenExpiredAt),
            sessionMsSinceLastUserActivity: msSince(this.timeline.lastUserActivityAt),
            accessTokenExp,
            accessTokenExpiresInSeconds,
            refreshTokenPresent,
            documentVisibility: document.visibilityState,
            refreshAttemptCount: this.timeline.refreshAttemptCount,
            lastRefreshOutcome: this.timeline.lastRefreshOutcome,
            config: configSnapshot,
        };
    }
}
